import { ExtendedArticleEntityType } from "../../store/Jobs/types";
import {
	articleClusterOptions,
	articleTypeOptions,
} from "../../lib/constants/options";
import { formatDate } from "../../lib/utils/utils-functions";

export type ArticleExportFormat = "xlsx" | "pdf" | "csv";

/**
 * Resolves a single article field to the same human-readable value shown in the
 * grid (cluster/type labels, Ja/Nein flags, formatted dates), so every export
 * format renders identical content.
 */
export const getArticleExportValue = (
	article: ExtendedArticleEntityType,
	field: string
): string | number => {
	switch (field) {
		case "cluster":
			return (
				articleClusterOptions.find((o) => o.value === article.cluster)
					?.label ??
				article.cluster ??
				""
			);
		case "type":
			return (
				articleTypeOptions.find((o) => o.value === article.type)
					?.label ??
				(article.type != null ? String(article.type) : "")
			);
		case "is_req_dr_tax":
			return article.is_req_dr_tax === true
				? "Ja"
				: article.is_req_dr_tax === false
					? "Nein"
					: "";
		case "modified_at":
			return article.custom ? formatDate(article.modified_at) : "Keine";
		default: {
			const value = (article as Record<string, unknown>)[field];
			if (value == null) return "";
			return value as string | number;
		}
	}
};

/**
 * Builds export row objects keyed by field, containing only the selected fields.
 */
export const buildArticleExportRows = (
	articles: ExtendedArticleEntityType[],
	fields: string[]
): Record<string, string | number>[] =>
	articles.map((article) => {
		const row: Record<string, string | number> = {};
		for (const field of fields) {
			row[field] = getArticleExportValue(article, field);
		}
		return row;
	});

const csvEscape = (value: string | number): string => {
	const str = String(value ?? "");
	if (/[";\n\r]/.test(str)) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
};

/**
 * Semicolon-delimited CSV with a UTF-8 BOM, so it opens cleanly in German Excel.
 */
export const buildArticleCsv = (
	rows: Record<string, string | number>[],
	columns: { field: string; title: string }[]
): string => {
	const header = columns.map((column) => csvEscape(column.title)).join(";");
	const body = rows.map((row) =>
		columns.map((column) => csvEscape(row[column.field])).join(";")
	);
	return "﻿" + [header, ...body].join("\r\n");
};

export const articleExportFilename = (
	format: ArticleExportFormat,
	date: Date
): string => {
	const stamp = date.toISOString().slice(0, 10);
	return `Artikel_${stamp}.${format}`;
};
